export type TranscriptFailureReason =
  | "never_admitted"
  | "bot_fatal"
  | "transcript_processing";

import { SavedAttachmentInfo } from "@/entities/attachments/types";
import { APIObject, Filters, GenericAggregations } from "@/services/api/types";
import { ISOStringFormat } from "date-fns";

export enum Activity {
  CALL = 1,
  EMAIL,
  TEXT,
  IN_PERSON_MEETING,
  ONLINE_MEETING,
  QUOTE_SENT,
  NDA_SENT,
  NDA_SIGNED,
  NOTE,
  SLACK_MESSAGE
}

export enum ActivityGroup {
  ALL = 0,
  MEETINGS,
  EMAILS,
  CALLS,
  OTHERS
}

export enum NewActivity {
  CALL,
  MEETING = 1,
  EMAIL,
  TEXT,
  NOTE,
  NDA,
  QUOTE,
  SLACK_MESSAGE
}

export enum MessageType {
  SMS = "sms",
  WHATSAPP = "whatsapp",
  LINKEDIN = "linkedin"
}

interface EmailNameType {
  email: string;
  name: string;
}

interface Summary {
  summary: string;
  painPoints?: { description: string; raisedBy: string }[] | null;
  actionItems?: { item: string; assignedTo: string }[] | string[] | null;
  keyTopics?: string[] | null;
  sentiment?: string | null;
}

interface ActivityParticipant {
  id: string;
  name?: string | null;
  role: "sales" | "prospect";
}

type ActivityDirection = "inbound" | "outbound";

interface Notes {
  date: ISOStringFormat;
  direction?: string | null;
  subject?: string;
  location?: string;
  summary?: Summary | null;
  endTime?: string;
  startTime?: string;
  messageType?: string;
  messageContent?: string;
  emailContent?: string;
  fromEmail?: EmailNameType;
  toEmails?: EmailNameType[];
  ccEmails?: EmailNameType[] | null;
  attachments?: SavedAttachmentInfo[] | null;
  attendees?: EmailNameType[] | null;
  meetingContent?: string;
  internalId?: string;
  recordingUrl?: string;
  recurringEventId?: string;
  isRecurring?: boolean;
  isCancelled?: boolean;
  participants?: ActivityParticipant[];
  sender?: ActivityParticipant;
  channelName?: string;
  channelId?: string;
  messageCount?: number;
  timeWindowStart?: string;
  timeWindowEnd?: string;
  messagesContent?: string;
  hasTranscript?: boolean;
  transcriptAttachmentId?: string;
  transcriptProcessingStatus?: "processing" | "failed";
  transcriptFailureReason?: TranscriptFailureReason;
}

interface ActivityData extends APIObject {
  createdAt: ISOStringFormat;
  dealId: string | null;
  leadId: string | null;
  id: string;
  peopleIds?: string[];
  companyId?: string;
  notes: Notes | null;
  type?: Activity;
  tags?: string[];
}

type ActivityUpdateNotes = Omit<Partial<Notes>, "fromEmail" | "summary"> & {
  date?: Notes["date"];
  fromEmail?: Partial<EmailNameType>;
  summary?: Partial<Summary> | null;
};

interface ActivityNotesPatch {
  date?: Notes["date"];
  subject?: string;
  location?: string | null;
  summary?: Pick<Summary, "summary">;
  attachments?: SavedAttachmentInfo[];
  startTime?: string;
  endTime?: string;
  emailContent?: string;
  toEmails?: EmailNameType[];
  ccEmails?: EmailNameType[];
  messageType?: string;
  messageContent?: string;
}

type ActivityUpdateData = Omit<Partial<ActivityData>, "notes"> & {
  notes?: ActivityUpdateNotes | null;
  notesPatch?: ActivityNotesPatch;
};

type ActivityDataInput = Omit<ActivityData, "createdAt" | "id">;

export interface ActivityFilters extends Filters {
  type?: number[];
}
type ActivitiesAggregationFieldNames = "type";

type ActivitiesAggregations =
  GenericAggregations<ActivitiesAggregationFieldNames>;

interface ActivityFlags {
  includeInvites?: boolean;
  includeCancelledMeetings?: boolean;
  includeRecurrentInvites?: boolean;
  requireTranscriptForMeeting?: boolean;
}

interface ActivityTypeCount {
  activityType: number;
  count: number;
}

interface EntityActivityRequest {
  activityTypes?: number[];
  fromDatetime?: string;
  toDatetime?: string;
  size?: number;
  direction?: ActivityDirection;
  includeRaw?: boolean;
  entityIds?: string[];
  peopleIds?: string[];
  activitiesTags?: string[];
  flags?: ActivityFlags;
}

interface EntityActivityResponse {
  activities: ActivityData[];
  total: number;
  fromDate?: string;
  toDate?: string;
  effectiveFromDate?: string;
  effectiveToDate?: string;
  countPerType: ActivityTypeCount[];
  effectiveCountPerType: ActivityTypeCount[];
}

// Lightweight banner gate: ids of the record's meeting activities whose
// transcript is still being analyzed (empty when none).
interface TranscriptProcessingResponse {
  processingActivityIds: string[];
}

export type {
  ActivityData,
  ActivityDirection,
  ActivityNotesPatch,
  ActivityUpdateData,
  Filters,
  ActivitiesAggregations,
  ActivityDataInput,
  Notes,
  ActivityParticipant,
  ActivityFlags,
  ActivityTypeCount,
  EntityActivityRequest,
  EntityActivityResponse,
  TranscriptProcessingResponse
};
